/**
 * Shared axios 401 refresh-token interceptor factory
 * Eliminates duplication between axiosCustomerService.ts and axiosOrderService.ts
 */
import {
  type AxiosInstance,
  type AxiosError,
  type InternalAxiosRequestConfig,
} from "axios";
import type { SignInResponse } from "../types/customer";

type FailedRequest = {
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
};

function processQueue(queue: FailedRequest[], error: unknown, token: string | null = null) {
  queue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token!);
    }
  });
}

/**
 * Attaches a 401 silent-refresh interceptor to the given axios instance.
 * Returns a setter to wire AuthContext callbacks.
 */
export function attachRefreshInterceptor(
  instance: AxiosInstance,
  getRefreshToken: () => Promise<SignInResponse>
): {
  setCallbacks: (
    onSuccess: ((result: SignInResponse) => void) | null,
    onFailure: (() => void) | null
  ) => void;
} {
  let isRefreshing = false;
  let failedQueue: FailedRequest[] = [];
  let onTokenRefreshed: ((result: SignInResponse) => void) | null = null;
  let onRefreshFailed: (() => void) | null = null;

  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & {
        _retry?: boolean;
      };

      // Only intercept 401 on authenticated (private) endpoints
      if (
        error.response?.status !== 401 ||
        originalRequest._retry ||
        originalRequest.url?.includes("/api/public/")
      ) {
        return Promise.reject(error);
      }

      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise<string>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((newToken) => {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return instance(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const data = await getRefreshToken();
        const newToken = data.access_token;

        // Sync React state
        onTokenRefreshed?.(data);

        // Unblock queued requests
        processQueue(failedQueue, null, newToken);
        failedQueue = [];

        // Retry the original failed request
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return instance(originalRequest);
      } catch (refreshError) {
        processQueue(failedQueue, refreshError, null);
        failedQueue = [];
        onRefreshFailed?.();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
  );

  return {
    setCallbacks: (onSuccess, onFailure) => {
      onTokenRefreshed = onSuccess;
      onRefreshFailed = onFailure;
    },
  };
}
